import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import moment from 'moment';
import UsageService from '../../services/usage.service';
import { UsageApiChip, UsageStatusChip, UsageBlockTitle, fmtDateTime, fmtTime, fmtInt } from '../usage/usage-widgets';
import { t } from '../usage/usage-i18n';
import DeviceIcon from './device-icon';

// ---------------------------------------------------------------------------
// Drawer de uma sessão ativa (item 7, 30/09/2026). Mostra o que a LINHA não mostra, para quem decide encerrar:
//   • De onde: país/estado/cidade, operadora e tipo de rede do IP (enriquecimento do item 6, via ranking por IP);
//   • O que fez nesta sessão: últimas requisições do usuário NESTE IP desde o login (histórico de uso, Reports);
//   • Outras sessões ativas do mesmo usuário (da lista já carregada): mesma pessoa em dois lugares?
// Reaproveita endpoints existentes; não há API nova. As ações (encerrar / desconectar de tudo) vêm da página por callback.
// ---------------------------------------------------------------------------

const ACTIVITY_PAGE = 20;
const KIND_LABEL = { isp: 'Operadora', hosting: 'Nuvem / datacenter', tor: 'Tor', private: 'Rede privada', unknown: 'Desconhecido' };
const KIND_COLOR = { hosting: '#E65100', tor: '#c62828' };

const shortUa = (ua) => {
  if (!ua) return '—';
  const m = ua.match(/(Edg|Chrome|Firefox|Safari|OPR)\/([\d.]+)/);
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return m ? `${m[1] === 'Edg' ? 'Edge' : m[1]} ${m[2].split('.')[0]}${os ? ` · ${os}` : ''}` : ua.slice(0, 40);
};

function Field({ label, children, mono }) {
  return (
    <Box style={{ marginBottom: 6 }}>
      <Typography variant="caption" style={{ color: '#888', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" style={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>{children}</Typography>
    </Box>
  );
}

export default function ActiveSessionDrawer({ intl, open, row, rows, lang, history, onClose, onRevoke, onRevokeAll, working }) {
  const tr = (id, fallback) => t(intl, id, fallback);
  const [where, setWhere] = useState({ loading: false, info: null, error: null });
  const [acts, setActs] = useState({ loading: false, rows: [], count: 0, error: null });

  // Ao trocar de sessão: busca "de onde" (ranking restrito ao IP, 7 dias: 1 linha com o enriquecimento) e "o que fez"
  // (histórico do usuário neste IP desde o login). Falha em um não derruba o outro.
  useEffect(() => {
    if (!row) return undefined;
    let alive = true;
    const today = moment().endOf('day').format('YYYY/MM/DD HH:mm:ss');
    if (row.ip) {
      setWhere({ loading: true, info: null, error: null });
      const f = { dateStart: moment().subtract(7, 'days').startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: today, api: '', ip: row.ip };
      UsageService.ipRanking(f, 1, 0, 1, 1)
        .then((r) => { if (alive) setWhere({ loading: false, info: (r.data && r.data[0]) || null, error: null }); })
        .catch((err) => { if (alive) setWhere({ loading: false, info: null, error: err.message }); });
    } else {
      setWhere({ loading: false, info: null, error: null });
    }
    if (row.userId) {
      setActs({ loading: true, rows: [], count: 0, error: null });
      const since = row.createdAt ? moment(row.createdAt).format('YYYY/MM/DD HH:mm:ss') : moment().startOf('day').format('YYYY/MM/DD HH:mm:ss');
      UsageService.activity({ userId: row.userId, ip: row.ip || '', dateStart: since, dateEnd: today, page: 1, pageSize: ACTIVITY_PAGE })
        .then((r) => { if (alive) setActs({ loading: false, rows: r.data || [], count: r.count || 0, error: null }); })
        .catch((err) => { if (alive) setActs({ loading: false, rows: [], count: 0, error: err.message }); });
    }
    return () => { alive = false; };
  }, [row && row.sessionId]);

  if (!row) return null;
  const others = (rows || []).filter((s) => s.userId === row.userId && s.sessionId !== row.sessionId && s.active);
  const info = where.info;
  const kindLabel = info && info.ipKind ? tr(`usage.ipKind.${info.ipKind}`, KIND_LABEL[info.ipKind] || info.ipKind) : null;
  const openActivity = () => {
    if (!history || !lang) return;
    const q = new URLSearchParams({ userId: String(row.userId || ''), userName: row.userName || '', start: moment(row.createdAt || undefined).format('YYYY-MM-DD'), end: moment().format('YYYY-MM-DD') });
    history.push(`/${lang}/usage/user-activity?${q.toString()}`);
  };

  return (
    // className usage-event-drawer: mexx-global-override.css neutraliza o verde do drawer de ações rápidas para esta classe.
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ className: 'usage-event-drawer', style: { width: 560, maxWidth: '100%', backgroundColor: '#fff' } }}>
      <Box style={{ padding: 16 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Chip size="small" label={row.active ? tr('session.status.active', 'ativa') : tr(`session.status.${row.status === 'expired' ? 'expired' : 'revoked'}`, row.status)} style={{ backgroundColor: row.active ? '#2e7d32' : '#9e9e9e', color: '#fff', fontWeight: 700 }} />
            {row.current && <Chip size="small" label={tr('session.current', 'esta')} style={{ backgroundColor: '#8BC34A', color: '#fff', fontWeight: 700 }} />}
            <DeviceIcon kind={row.deviceKind} intl={intl} t={t} />
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Typography variant="h6" style={{ marginTop: 8 }}>{row.userName || `#${row.userId}`}</Typography>
        <Typography variant="caption" style={{ color: '#888' }}>{row.userEmail || ''}</Typography>
        <Divider style={{ margin: '12px 0' }} />

        <Field label={tr('session.col.started', 'Login')}>{fmtDateTime(row.createdAt)}{row.loginKind ? ` · ${row.loginKind}` : ''}</Field>
        <Field label={tr('session.col.lastRefresh', 'Última renovação')}>{row.lastRefreshAt ? `${fmtDateTime(row.lastRefreshAt)} (${row.refreshCount}×)` : '—'}</Field>
        <Field label="IP" mono>{row.ip || '—'}</Field>
        <Field label={tr('session.col.browser', 'Navegador')}><Tooltip title={row.userAgent || ''}><span>{shortUa(row.userAgent)}</span></Tooltip></Field>
        <Field label={tr('session.drawer.sessionId', 'Id da sessão')} mono>{row.sessionId}</Field>

        <Divider style={{ margin: '12px 0' }} />
        <UsageBlockTitle title={tr('session.drawer.where', 'De onde')} subtitle={tr('session.drawer.where.subtitle', 'país, operadora e tipo de rede do IP (enriquecimento, item 6)')} />
        {where.loading && <CircularProgress size={18} color="secondary" />}
        {!where.loading && where.error && <Typography variant="caption" style={{ color: '#c62828' }}>{where.error}</Typography>}
        {!where.loading && !where.error && !info && <Typography variant="caption" style={{ color: '#888' }}>{tr('session.drawer.where.none', 'Sem dado de enriquecimento para este IP ainda (o ciclo roda de hora em hora).')}</Typography>}
        {info && (
          <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
            {info.countryName && <Chip size="small" label={`${info.countryName}${info.region ? ` · ${info.region}` : ''}${info.city ? ` · ${info.city}` : ''}`} style={{ backgroundColor: info.countryCode && info.countryCode !== 'BR' ? '#E65100' : undefined, color: info.countryCode && info.countryCode !== 'BR' ? '#fff' : undefined }} />}
            {info.asnOrg && <Chip size="small" variant="outlined" label={`${info.asnOrg}${info.asn ? ` (AS${info.asn})` : ''}`} />}
            {kindLabel && <Chip size="small" label={kindLabel} style={{ backgroundColor: KIND_COLOR[info.ipKind], color: KIND_COLOR[info.ipKind] ? '#fff' : undefined }} />}
            <Chip size="small" variant="outlined" label={`${fmtInt(info.requests)} ${tr('session.drawer.where.requests7d', 'req. em 7 dias')} · ${fmtInt(info.users)} ${tr('usage.col.users', 'usuários')}`} />
          </Box>
        )}

        <Divider style={{ margin: '12px 0' }} />
        <UsageBlockTitle title={tr('session.drawer.activity', 'O que fez nesta sessão')} subtitle={`${tr('session.drawer.activity.subtitle', 'requisições deste usuário neste IP desde o login')}${acts.count ? ` · ${fmtInt(acts.count)}` : ''}`}
          right={<Button size="small" endIcon={<OpenInNewIcon />} onClick={openActivity} disabled={!row.userId}>{tr('session.drawer.activity.open', 'Atividade completa')}</Button>} />
        {acts.loading && <CircularProgress size={18} color="secondary" />}
        {!acts.loading && acts.error && <Typography variant="caption" style={{ color: '#c62828' }}>{acts.error}</Typography>}
        {!acts.loading && !acts.error && !acts.rows.length && <Typography variant="caption" style={{ color: '#888' }}>{tr('session.drawer.activity.none', 'Nenhuma requisição registrada nesta sessão ainda.')}</Typography>}
        {acts.rows.length > 0 && (
          <Box style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow><TableCell>{tr('usage.col.when', 'Quando')}</TableCell><TableCell>API</TableCell><TableCell>{tr('usage.col.route', 'Rota')}</TableCell><TableCell align="right">{tr('usage.col.status', 'Status')}</TableCell></TableRow></TableHead>
              <TableBody>
                {acts.rows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtTime(a.occurredAt)}</TableCell>
                    <TableCell><UsageApiChip api={a.api} /></TableCell>
                    <TableCell style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.route || a.path || ''}>{a.actionKind === 'screen' ? a.path : `${a.httpMethod || ''} ${a.route || a.path || ''}`}</TableCell>
                    <TableCell align="right">{a.actionKind === 'screen' ? <Chip size="small" label={tr('usage.kind.screen', 'tela')} /> : <UsageStatusChip code={a.statusCode} />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Divider style={{ margin: '12px 0' }} />
        <UsageBlockTitle title={tr('session.drawer.others', 'Outras sessões ativas deste usuário')} subtitle={others.length ? `${others.length}` : tr('session.drawer.others.none', 'nenhuma nesta lista')} />
        {others.length > 0 && (
          <Table size="small">
            <TableHead><TableRow><TableCell>{tr('session.col.started', 'Login')}</TableCell><TableCell>IP</TableCell><TableCell>{tr('session.col.browser', 'Navegador')}</TableCell></TableRow></TableHead>
            <TableBody>
              {others.map((s) => (
                <TableRow key={s.sessionId}>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(s.createdAt)}</TableCell>
                  <TableCell style={{ fontFamily: 'monospace' }}>{s.ip || '—'}</TableCell>
                  <TableCell><DeviceIcon kind={s.deviceKind} intl={intl} t={t} />{shortUa(s.userAgent)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {row.active && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Box display="flex" style={{ gap: 8 }}>
              <Button size="small" variant="outlined" color="error" startIcon={<LogoutIcon />} disabled={working} onClick={() => onRevoke(row)}>{tr('session.revokeOne.button', 'Encerrar sessão')}</Button>
              <Button size="small" variant="contained" color="error" startIcon={<PowerSettingsNewIcon />} disabled={working} onClick={() => onRevokeAll(row)}>{tr('session.revokeAll.button', 'Desconectar de tudo')}</Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
